import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { useAuthStore } from '@/stores/auth'
import { storage } from '@/composables/useStorage'
import { useMenuStore } from '@/stores/menu'

export const useCartStore = defineStore('cart', () => {
  const items = ref([])
  const promoCode = ref(null)
  const discount = ref(0)
  const discountType = ref('fixed') // 'fixed' или 'percent'
  const deliveryPriceAmount = ref(0)
  const isLoading = ref(false)
  const { get, post, put, delete: del } = useApi()
  const { showToast } = useToast()
  const authStore = useAuthStore()
  
  // Функция для обогащения товара изображением из menuStore
  const enrichItemWithImage = (item) => {
    // Если изображение уже есть, возвращаем как есть
    if (item.image_url || item.image) {
      return item
    }
    
    // Пытаемся найти товар в menuStore
    // Используем useMenuStore() внутри функции, чтобы получить актуальный экземпляр
    let menuStore
    try {
      menuStore = useMenuStore()
    } catch (e) {
      // Если menuStore недоступен, возвращаем item как есть
      return item
    }
    
    if (item.product_id && menuStore.products && menuStore.products.length > 0) {
      let product = menuStore.getProduct(item.product_id)
      
      // Пробуем найти по строковому ID
      if (!product && typeof item.product_id === 'string') {
        product = menuStore.products.find(p => String(p.id) === item.product_id)
      }
      
      // Пробуем найти по числовому ID
      if (!product && typeof item.product_id === 'number') {
        product = menuStore.products.find(p => Number(p.id) === item.product_id)
      }
      
      if (product) {
        const productImage = product.image || 
                           (product.imageLinks && product.imageLinks[0]) ||
                           product.image_url || 
                           null
        
        if (productImage) {
          return {
            ...item,
            image: productImage,
            image_url: productImage
          }
        }
      }
    }
    
    return item
  }

  // Сохранение локальной корзины в localStorage для неавторизованных пользователей
  const saveLocalCart = () => {
    if (!authStore.isAuthenticated) {
      storage.set('local_cart', {
        items: items.value,
        promoCode: promoCode.value,
        discount: discount.value,
        discountType: discountType.value
      })
    }
  }

  // Загрузка локальной корзины из localStorage
  const loadLocalCart = () => {
    if (!authStore.isAuthenticated) {
      try {
        const localCart = storage.get('local_cart')
        if (localCart) {
          // Обогащаем товары изображениями из menuStore (на случай, если они были потеряны)
          const enrichedItems = (localCart.items || []).map(item => enrichItemWithImage(item))
          items.value = enrichedItems
          promoCode.value = localCart.promoCode || null
          discount.value = localCart.discount || 0
          discountType.value = localCart.discountType || 'fixed'
          return true
        }
      } catch (e) {
        console.error('[Cart] Load local cart error:', e)
      }
    }
    return false
  }

  // Синхронизация локальной корзины с серверной при авторизации
  const syncLocalCartToServer = async () => {
    if (!authStore.isAuthenticated) return
    
    try {
      const localCart = storage.get('local_cart')
      if (localCart && localCart.items && localCart.items.length > 0) {
        // Добавляем все товары из локальной корзины в серверную
        for (const item of localCart.items) {
          try {
            await post('cart/item', {
              product_id: item.product_id,
              quantity: item.quantity,
              modifiers: item.modifiers || [],
              selected_modifiers: item.selected_modifiers || []
            })
          } catch (e) {
            console.error('[Cart] Sync item error:', e)
          }
        }
        
        // Применяем промокод, если был
        if (localCart.promoCode) {
          try {
            await post('cart/promo', { code: localCart.promoCode })
          } catch (e) {
            console.error('[Cart] Sync promo error:', e)
          }
        }
        
        // Очищаем локальную корзину
        storage.remove('local_cart')
        
        // Загружаем серверную корзину
        await loadCart()
      }
    } catch (e) {
      console.error('[Cart] Sync local cart error:', e)
    }
  }

  // Отслеживаем изменения для сохранения локальной корзины
  watch([items, promoCode, discount], () => {
    saveLocalCart()
  }, { deep: true })

  // Computed
  const subtotal = computed(() => {
    return items.value.reduce((sum, item) => {
      const itemPrice = item.price || 0
      const modifiersPrice = (item.modifiers || []).reduce((mSum, mod) => {
        return mSum + (mod.price || 0) * (mod.quantity || 1)
      }, 0)
      return sum + (itemPrice + modifiersPrice) * (item.quantity || 1)
    }, 0)
  })

  const deliveryPrice = computed(() => deliveryPriceAmount.value)

  const total = computed(() => {
    let total = subtotal.value - discount.value + deliveryPrice.value
    return Math.max(0, total)
  })

  const itemCount = computed(() => {
    return items.value.reduce((sum, item) => sum + (item.quantity || 1), 0)
  })

  const isEmpty = computed(() => items.value.length === 0)

  const setDeliveryPrice = (price) => {
    const normalized = Number(price)
    deliveryPriceAmount.value = Number.isFinite(normalized)
      ? Math.max(0, normalized)
      : 0
  }

  const clearDeliveryPrice = () => {
    deliveryPriceAmount.value = 0
  }

  // Загрузка корзины
  const loadCart = async () => {
    isLoading.value = true
    try {
      // Если не авторизован, загружаем из localStorage
      if (!authStore.isAuthenticated) {
        loadLocalCart()
        isLoading.value = false
        return true
      }
      
      const response = await get('cart')
      
      if (response.success && response.cart) {
        // Обогащаем товары изображениями из menuStore
        const enrichedItems = (response.cart.items || []).map(item => enrichItemWithImage(item))
        items.value = enrichedItems
        promoCode.value = response.cart.promo_code || null
        discount.value = response.cart.promo_discount || 0
        discountType.value = response.cart.promo_discount_type || 'fixed'
        return true
      }
      
      // Если корзина пустая, это нормально
      items.value = []
      promoCode.value = null
      discount.value = 0
      return true
    } catch (error) {
      console.error('[Cart] Load cart error:', error)
      // Если ошибка 401 (не авторизован), загружаем из localStorage
      if (error.message && error.message.includes('401')) {
        loadLocalCart()
        return true
      }
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // Добавить товар в корзину
  const addItem = async (
    product,
    quantity = 1,
    modifiers = [],
    selectedModifiers = []
  ) => {
    // Сохраняем текущее состояние для отката
    const previousItems = JSON.parse(JSON.stringify(items.value))
    
    try {
      // Если не авторизован, добавляем в локальную корзину
      if (!authStore.isAuthenticated) {
        const existingIndex = items.value.findIndex(
          (i) =>
            i.product_id === product.id &&
            JSON.stringify(i.modifiers || []) === JSON.stringify(modifiers)
        )
        
        if (existingIndex >= 0) {
          items.value[existingIndex].quantity += quantity
        } else {
          // Сохраняем изображение товара при добавлении
          const productImage = product.image || 
                               (product.imageLinks && product.imageLinks[0]) ||
                               product.image_url || 
                               null
          
          items.value.push({
            id: `${product.id}_${Date.now()}`, // Временный ID для локальной корзины
            product_id: product.id,
            name: product.name,
            price: product.price,
            quantity: quantity,
            modifiers: modifiers,
            selected_modifiers: selectedModifiers,
            image: productImage,
            image_url: productImage
          })
        }
        
        saveLocalCart()
        showToast('Товар добавлен в корзину', 'success')
        return true
      }
      
      // Оптимистичное обновление для авторизованных пользователей
      const existingIndex = items.value.findIndex(
        (i) =>
          i.product_id === product.id &&
          JSON.stringify(i.modifiers || []) === JSON.stringify(modifiers)
      )
      
      if (existingIndex >= 0) {
        items.value[existingIndex].quantity += quantity
      } else {


        const productImage = product.image || 
                             (product.imageLinks && product.imageLinks[0]) ||
                             product.image_url || 
                             null
        
        items.value.push({
          id: `temp_${product.id}_${Date.now()}`, // Временный ID
          product_id: product.id,
          name: product.name,
          price: product.price,
          quantity: quantity,
          modifiers: modifiers,
          selected_modifiers: selectedModifiers,
          image: productImage,
          image_url: productImage
        })
      }
      
      // Отправляем запрос на сервер
      const response = await post('cart/item', {
        product_id: product.id,
        quantity: quantity,
        modifiers: modifiers,
        selected_modifiers: selectedModifiers
      })
      
      if (response.success) {
        // Обновляем корзину с сервера для получения правильных ID
        await loadCart()
        showToast('Товар добавлен в корзину', 'success')
        return true
      }
      
      throw new Error(response.message || 'Ошибка добавления товара')
    } catch (error) {
      // Откатываем изменения при ошибке
      items.value = previousItems
      console.error('[Cart] Add item error:', error)
      showToast(error.message || 'Ошибка добавления товара', 'error')
      throw error
    }
  }

  // Хелпер для поиска индекса товара по индексу, id, product_id или объекту
  const resolveItemIndex = (target) => {
    if (typeof target === 'number' && target >= 0 && target < items.value.length) {
      return target
    }
    const idx = items.value.findIndex(item => 
      item === target ||
      (item.id !== undefined && String(item.id) === String(target)) ||
      (item.product_id !== undefined && String(item.product_id) === String(target))
    )
    return idx
  }

  // Обновить количество товара
  const updateQuantity = async (target, quantity) => {
    const itemIndex = resolveItemIndex(target)
    if (itemIndex < 0 || !items.value[itemIndex]) {
      console.warn('[Cart] Item not found for updateQuantity:', target)
      return false
    }

    if (quantity <= 0) {
      return removeItem(itemIndex)
    }

    // Сохраняем текущее состояние для отката
    const previousItems = JSON.parse(JSON.stringify(items.value))

    try {
      // Если не авторизован, обновляем локальную корзину
      if (!authStore.isAuthenticated) {
        if (items.value[itemIndex]) {
          items.value[itemIndex].quantity = quantity
          saveLocalCart()
        }
        return true
      }
      
      // Оптимистичное обновление
      if (items.value[itemIndex]) {
        items.value[itemIndex].quantity = quantity
      }
      
      const response = await put(`cart/item/${itemIndex}`, {
        quantity: quantity
      })
      
      if (response.success) {
        await loadCart()
        return true
      }
      
      throw new Error(response.message || 'Ошибка обновления количества')
    } catch (error) {
      // Откатываем изменения при ошибке
      items.value = previousItems
      console.error('[Cart] Update quantity error:', error)
      showToast(error.message || 'Ошибка обновления количества', 'error')
      throw error
    }
  }

  // Псевдоним для совместимости
  const updateItemQuantity = (target, quantity) => updateQuantity(target, quantity)

  // Удалить товар из корзины
  const removeItem = async (target) => {
    const itemIndex = resolveItemIndex(target)
    if (itemIndex < 0 || !items.value[itemIndex]) {
      console.warn('[Cart] Item not found for removeItem:', target)
      return false
    }

    // Сохраняем текущее состояние для отката
    const previousItems = JSON.parse(JSON.stringify(items.value))

    try {
      // Если не авторизован, удаляем из локальной корзины
      if (!authStore.isAuthenticated) {
        items.value.splice(itemIndex, 1)
        saveLocalCart()
        showToast('Товар удален из корзины', 'info')
        return true
      }
      
      // Оптимистичное обновление
      items.value.splice(itemIndex, 1)
      
      const response = await del(`cart/item/${itemIndex}`)
      
      if (response.success) {
        await loadCart()
        showToast('Товар удален из корзины', 'info')
        return true
      }
      
      throw new Error(response.message || 'Ошибка удаления товара')
    } catch (error) {
      // Откатываем изменения при ошибке
      items.value = previousItems
      console.error('[Cart] Remove item error:', error)
      showToast(error.message || 'Ошибка удаления товара', 'error')
      throw error
    }
  }

  // Применить промокод
  const applyPromo = async (code) => {
    try {
      // Если не авторизован, сохраняем промокод локально (валидация будет при оформлении заказа)
      if (!authStore.isAuthenticated) {
        promoCode.value = code
        discount.value = 0 // Скидка будет рассчитана на сервере при оформлении заказа
        discountType.value = 'fixed'
        saveLocalCart()
        showToast('Промокод применен', 'success')
        return true
      }
      
      const response = await post('cart/promo', {
        code: code
      })
      
      if (response.success) {
        promoCode.value = response.cart?.promo_code || code
        discount.value = response.discount || response.cart?.promo_discount || 0
        discountType.value = response.discount_type || 'fixed'
        showToast('Промокод применен', 'success')
        await loadCart() // Перезагружаем для обновления скидки
        return true
      }
      
      throw new Error(response.message || 'Промокод недействителен')
    } catch (error) {
      console.error('[Cart] Apply promo error:', error)
      showToast(error.message || 'Ошибка применения промокода', 'error')
      throw error
    }
  }

  // Удалить промокод
  const removePromo = async () => {
    try {
      // Если не авторизован, удаляем промокод из локальной корзины
      if (!authStore.isAuthenticated) {
        promoCode.value = null
        discount.value = 0
        saveLocalCart()
        return true
      }
      
      const response = await del('cart/promo')
      
      if (response.success) {
        promoCode.value = null
        discount.value = 0
        await loadCart()
        return true
      }
      
      throw new Error(response.message || 'Ошибка удаления промокода')
    } catch (error) {
      console.error('[Cart] Remove promo error:', error)
      throw error
    }
  }

  // Очистить корзину
  const clearCart = async () => {
    try {
      // Если не авторизован, очищаем локальную корзину
      if (!authStore.isAuthenticated) {
        items.value = []
        promoCode.value = null
        discount.value = 0
        clearDeliveryPrice()
        storage.remove('local_cart')
        return true
      }
      
      const response = await del('cart')
      
      if (response.success) {
        items.value = []
        promoCode.value = null
        discount.value = 0
        clearDeliveryPrice()
        return true
      }
      
      throw new Error(response.message || 'Ошибка очистки корзины')
    } catch (error) {
      console.error('[Cart] Clear cart error:', error)
      throw error
    }
  }

  return {
    // State
    items,
    promoCode,
    discount,
    discountType,
    isLoading,
    // Computed
    subtotal,
    deliveryPrice,
    total,
    itemCount,
    isEmpty,
    // Actions
    loadCart,
    addItem,
    updateQuantity,
    updateItemQuantity,
    removeItem,
    applyPromo,
    removePromo,
    clearCart,
    setDeliveryPrice,
    clearDeliveryPrice,
    syncLocalCartToServer
  }
})
